import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCartStore } from '../store/cartStore';
import { useAuthStore } from '../store/authStore';
import { productApi } from '../api';
import { Product, CartItem } from '../types';
import Loading from '../components/common/Loading';
import ProductCard from '../components/product/ProductCard';
import { toImageUrl } from '../utils/image';
import { formatPrice } from '../utils/price';

const CartPage: React.FC = () => {
  const { t } = useTranslation();
  const { cart, fetchCart, updateItem, removeItem, updateGroupBuyItem, removeGroupBuyItem } = useCartStore();
  const { user } = useAuthStore();
  const navigate = useNavigate();

  const changeQty = (item: CartItem, qty: number) =>
    item.kind === 'GROUP_BUY'
      ? updateGroupBuyItem(item.groupBuyId!, qty, item.variantId)
      : updateItem(item.productId!, qty, item.variantId);
  const removeLine = (item: CartItem) =>
    item.kind === 'GROUP_BUY'
      ? removeGroupBuyItem(item.groupBuyId!, item.variantId)
      : removeItem(item.productId!, item.variantId);
  const lineKey = (i: CartItem) => `${i.kind}:${i.productId ?? i.groupBuyId}:${i.variantId ?? 0}`;
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [newArrivals, setNewArrivals] = useState<Product[]>([]);
  const [bestSellers, setBestSellers] = useState<Product[]>([]);

  // 카트 내용물(개수)이 바뀌면 구매 가능한 항목 전체를 기본 선택
  useEffect(() => {
    if (cart) setSelected(new Set(cart.items.filter(i => i.available).map(lineKey)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart?.items.length]);

  const toggle = (i: CartItem) => setSelected(prev => {
    const next = new Set(prev);
    const k = lineKey(i);
    next.has(k) ? next.delete(k) : next.add(k);
    return next;
  });

  useEffect(() => {
    if (!user) return;
    fetchCart();
    productApi.getList({ sortType: 'new', size: 8 }).then(r => setNewArrivals(r.data.data.content));
    productApi.getList({ sortType: 'best', size: 8 }).then(r => setBestSellers(r.data.data.content));
  }, []);

  if (!user) {
    return (
      <div style={styles.empty}>
        <p style={styles.emptyIcon}>🔒</p>
        <p style={styles.emptyText}>{t('cart.loginRequired')}</p>
        <Link to="/login" style={styles.shopBtn}>{t('cart.goLogin')}</Link>
      </div>
    );
  }

  // filter out items already in cart
  const cartIds = new Set(cart?.items.map(i => i.productId) ?? []);
  const likeList = newArrivals.filter(p => !cartIds.has(p.id)).slice(0, 4);
  const likeIds = new Set(likeList.map(p => p.id));
  const alsoList = bestSellers.filter(p => !cartIds.has(p.id) && !likeIds.has(p.id)).slice(0, 4);

  if (!cart) return <Loading />;

  if (cart.items.length === 0) {
    return (
      <div style={styles.empty}>
        <p style={styles.emptyIcon}>🛒</p>
        <p style={styles.emptyText}>{t('cart.empty')}</p>
        <Link to="/products" style={styles.shopBtn}>{t('cart.goShopping')}</Link>
      </div>
    );
  }

  const availableItems = cart.items.filter(i => i.available);
  const selectedItems = availableItems.filter(i => selected.has(lineKey(i)));
  const selectedSubtotal = selectedItems.reduce((s, i) => s + i.lineTotal, 0);
  const selectedCount = selectedItems.reduce((s, i) => s + i.quantity, 0);
  const allSelected = availableItems.length > 0 && selectedItems.length === availableItems.length;
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(availableItems.map(lineKey)));
  const goCheckout = () => {
    if (selectedItems.length === 0) return;
    navigate('/checkout', { state: { selectedKeys: selectedItems.map(lineKey) } });
  };

  return (
    <div style={styles.page}>
      <h1 style={styles.title}>{t('cart.title')}</h1>

      <div style={styles.layout}>
        <div style={styles.itemList}>
          {availableItems.length > 0 && (
            <label style={styles.selectAllRow}>
              <input type="checkbox" checked={allSelected} onChange={toggleAll} />
              <span>{t('cart.selectAll')} ({selectedItems.length}/{availableItems.length})</span>
            </label>
          )}
          {cart.items.map(item => (
            <div key={lineKey(item)} style={{ ...styles.item, opacity: item.available ? 1 : 0.5 }}>
              {item.available && (
                <input
                  type="checkbox"
                  checked={selected.has(lineKey(item))}
                  onChange={() => toggle(item)}
                  style={styles.itemCheck}
                />
              )}
              <img src={toImageUrl(item.thumbnailUrl)} alt={item.name} style={styles.itemImg} />
              <div style={styles.itemInfo}>
                <p style={styles.itemName}>
                  {item.kind === 'GROUP_BUY' && <span style={styles.gbBadge}>GROUP BUY</span>}
                  {item.name}
                </p>
                {item.variantName && <p style={styles.itemVariant}>{item.variantName}</p>}
                {item.discountPercent ? (
                  <p style={styles.itemPrice}>
                    <span style={styles.salePrice}>{formatPrice(item.unitPrice, 'USD')}</span>
                    <span style={styles.origPrice}>{formatPrice(item.originalUnitPrice, 'USD')}</span>
                  </p>
                ) : (
                  <p style={styles.itemPrice}>{formatPrice(item.unitPrice, 'USD')}</p>
                )}
                {!item.available && (
                  <p style={styles.unavailable}>{item.unavailableReason}</p>
                )}
              </div>
              <div style={styles.qtyControl}>
                <button style={styles.qtyBtn} onClick={() => changeQty(item, item.quantity - 1)}>−</button>
                <span style={styles.qty}>{item.quantity}</span>
                <button style={styles.qtyBtn} onClick={() => changeQty(item, item.quantity + 1)}>+</button>
              </div>
              <p style={styles.itemTotal}>{formatPrice(item.lineTotal, 'USD')}</p>
              <button style={styles.removeBtn} onClick={() => removeLine(item)}>✕</button>
            </div>
          ))}
        </div>

        <div style={styles.summary}>
          <h3 style={styles.summaryTitle}>{t('cart.title')}</h3>
          <div style={styles.summaryRow}>
            <span>{t('cart.subtotal')} ({selectedCount})</span>
            <span>{formatPrice(selectedSubtotal, 'USD')}</span>
          </div>
          <div style={{ ...styles.summaryRow, fontSize: 12, color: '#999' }}>
            <span>{t('cart.shipping')}</span>
            <span>{t('cart.calcAtCheckout')}</span>
          </div>
          <button
            style={{ ...styles.checkoutBtn, ...(selectedItems.length === 0 ? { opacity: 0.5, cursor: 'not-allowed' } : {}) }}
            onClick={goCheckout}
            disabled={selectedItems.length === 0}
          >
            {t('cart.checkout')} ({selectedCount})
          </button>
        </div>
      </div>

      {/* 추천 섹션 */}
      {likeList.length > 0 && (
        <section style={styles.recSection}>
          <h2 style={styles.recTitle}>{t('cartRecommend.youMightLike')}</h2>
          <div style={styles.recGrid}>
            {likeList.map(p => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}

      {alsoList.length > 0 && (
        <section style={styles.recSection}>
          <h2 style={styles.recTitle}>{t('cartRecommend.customersBought')}</h2>
          <div style={styles.recGrid}>
            {alsoList.map(p => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  page: { maxWidth: 1100, margin: '40px auto', padding: '0 20px 60px' },
  title: { fontSize: 24, fontWeight: 700, marginBottom: 32 },
  layout: { display: 'flex', gap: 32, alignItems: 'flex-start' },
  itemList: { flex: 1, display: 'flex', flexDirection: 'column', gap: 16 },
  selectAllRow: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#666', cursor: 'pointer', padding: '0 4px' },
  itemCheck: { width: 18, height: 18, cursor: 'pointer', flexShrink: 0 },
  item: { display: 'flex', alignItems: 'center', gap: 16, padding: 20, background: '#fff', borderRadius: 12, border: '1px solid #f0f0f0' },
  itemImg: { width: 80, height: 80, objectFit: 'cover', borderRadius: 8, background: '#f5f5f5' },
  itemInfo: { flex: 1 },
  itemName: { fontSize: 14, fontWeight: 500, marginBottom: 4 },
  gbBadge: { fontSize: 10, fontWeight: 700, color: '#fff', background: '#b39ddb', borderRadius: 8, padding: '2px 6px', marginRight: 6 },
  itemVariant: { fontSize: 12, color: '#7e57c2', marginBottom: 4 },
  itemPrice: { fontSize: 13, color: '#888', display: 'flex', gap: 6, alignItems: 'center' },
  salePrice: { color: '#b39ddb', fontWeight: 700 },
  origPrice: { textDecoration: 'line-through', fontSize: 11 },
  unavailable: { fontSize: 11, color: '#e53935', marginTop: 4 },
  qtyControl: { display: 'flex', alignItems: 'center', border: '1px solid #e8e8e8', borderRadius: 6 },
  qtyBtn: { width: 32, height: 32, background: 'none', border: 'none', fontSize: 16, cursor: 'pointer' },
  qty: { width: 40, textAlign: 'center', fontSize: 14, fontWeight: 600 },
  itemTotal: { fontSize: 16, fontWeight: 700, minWidth: 80, textAlign: 'right' },
  removeBtn: { background: 'none', border: 'none', color: '#ccc', fontSize: 16, cursor: 'pointer' },
  summary: { width: 300, background: '#fafafa', borderRadius: 12, padding: 24, position: 'sticky', top: 80, border: '1px solid #f0f0f0' },
  summaryTitle: { fontSize: 16, fontWeight: 700, marginBottom: 20 },
  summaryRow: { display: 'flex', justifyContent: 'space-between', fontSize: 14, color: '#555', marginBottom: 12 },
  summaryDivider: { height: 1, background: '#e8e8e8', margin: '16px 0' },
  summaryTotal: { fontSize: 16, fontWeight: 700, color: '#333' },
  checkoutBtn: { width: '100%', padding: '16px', background: '#b39ddb', color: '#fff', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 600, marginTop: 20, cursor: 'pointer' },
  empty: { textAlign: 'center', padding: '100px 20px' },
  emptyIcon: { fontSize: 64, marginBottom: 16 },
  emptyText: { fontSize: 18, color: '#888', marginBottom: 24 },
  shopBtn: { display: 'inline-block', padding: '12px 32px', background: '#b39ddb', color: '#fff', borderRadius: 8 },
  recSection: { marginTop: 56, borderTop: '1px solid #f0f0f0', paddingTop: 40 },
  recTitle: { fontSize: 18, fontWeight: 700, color: '#333', marginBottom: 20 },
  recGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 },
};

export default CartPage;
